// The two "you can't get in" pages (spec, The no-access and unavailable pages).
import { useState } from 'react';

import { useSession, useSignOut } from './session/SessionProvider.jsx';

function Frame({ title, children }) {
  return (
    <div className="grid min-h-screen place-items-center px-6">
      <div className="max-w-md text-center">
        <h1 className="text-lg font-semibold">{title}</h1>
        {children}
      </div>
    </div>
  );
}

/** /no-access: a sign-in refused for having no role. There is no session, so no name. */
export function NoAccessSignedOut() {
  return (
    <Frame title="No access yet">
      <p className="mt-2 text-sm text-ink-secondary">
        The Microsoft account you used isn’t assigned to any Adora Ops module yet. Ask IT to
        assign you a role, then sign in again.
      </p>
      {/* A plain link: /auth/login is the server's, and it asks which account to use. */}
      <a className="btn-primary mt-4 inline-block" href="/auth/login">Try a different account</a>
    </Frame>
  );
}

/** Signed in, but nothing is available: no role at all, or roles only in modules not on here. */
export function NoAccessSignedIn({ reason }) {
  const user = useSession();
  const signOut = useSignOut();
  const [signingOut, setSigningOut] = useState(false);
  const [signOutError, setSignOutError] = useState(null);

  // A sign-out that FAILED must not look like one that worked, here as in
  // PlatformLayout's band: someone on a shared desk who clicked it walks away believing
  // they are out. A stale CSRF token (403) is the realistic failure, and a reload fixes it.
  async function handleSignOut() {
    setSigningOut(true);
    setSignOutError(null);
    try {
      await signOut();
    } catch (error) {
      setSigningOut(false);
      setSignOutError(error?.message ?? 'Sign-out failed. Reload the page and try again.');
    }
  }

  return (
    <Frame title={reason === 'not-enabled' ? 'Not available yet' : 'No access yet'}>
      <p className="mt-2 text-sm text-ink-secondary">
        {reason === 'not-enabled'
          ? <>You’re signed in as {user.email}. Your modules aren’t available on Adora Ops yet.</>
          : <>You’re signed in as {user.email}, but this account isn’t assigned to any Adora Ops
            module yet. Ask IT to assign you a role, then sign in again.</>}
      </p>
      {!user.auth_is_stub && (
        <button type="button" className="btn-ghost mt-4" onClick={handleSignOut} disabled={signingOut}>
          {signingOut ? 'Signing out…' : 'Sign out'}
        </button>
      )}
      {signOutError && (
        <p role="alert" className="mt-2 text-sm text-rose-700 dark:text-rose-300">
          You are still signed in. {signOutError}
        </p>
      )}
    </Frame>
  );
}
